/**
 * Alvos de avaliação de uma carona — QUEM pode avaliar QUEM.
 *
 * Módulo PURO (sem `mongodb`, sem I/O): importado por componentes client
 * (`/trip/[id]`, `/trip-summary/[id]`) e pelos testes. Mesma premissa de
 * `lib/my-rides.ts`: a decisão mora num módulo testável e o componente só
 * desenha.
 *
 * O QUE ESTE MÓDULO NÃO DECIDE: se a carona foi liquidada e se a janela de
 * avaliação está aberta. Isso vem do servidor (`GET /api/ratings` devolve
 * `isSettled`, `isExpired` e `windowEndsAt`) e da regra pura de
 * `lib/my-rides.ts` (`ratingCta`). Aqui mora só a LISTA de alvos — a mesma
 * que o card de `/trip/[id]` e o de `/trip-summary/[id]` precisam mostrar.
 *
 * REGRAS (as mesmas do servidor — `resolveRatingDirection`, `lib/rating-utils`):
 * - MOTORISTA avalia passageiro com reserva CONFIRMADA. Pedido pendente não é
 *   participante: o passageiro pode ser recusado a qualquer momento.
 * - PASSAGEIRO avalia o MOTORISTA, e só se ele tem reserva CONFIRMADA.
 * - Ninguém avalia a si mesmo, e quem já avaliou não aparece de novo.
 */

export interface RatingTargetReservation {
  passengerId?: string | null
  name?: string | null
  avatar?: string | null
  status?: string | null
  origin?: string | null
  destination?: string | null
  segment?: string | null
}

export interface RatingTarget {
  /** `passengerId` do passageiro ou `driverId` do motorista. */
  id: string
  name: string
  avatar: string | null
  /** Rótulo do trecho. `null` quando não dá para montar. */
  label: string | null
  /** Quantos assentos a pessoa ocupou — 2 assentos viram 1 linha com "+1". */
  count: number
}

/**
 * Primeiro pedaço do endereço ("Rua X, 123 - Centro, Curitiba" → "Rua X").
 *
 * O card mostra o endereço curto porque a linha é truncada: o endereço inteiro
 * não cabe e o usuário lê melhor o começo.
 */
export function shortPlaceName(place?: string | null): string | null {
  if (!place) return null
  const first = place.split(", ")[0]?.trim()
  return first ? first : null
}

/** "Origem → Destino" no formato curto usado nas linhas do card. */
export function shortSegmentLabel(res: {
  origin?: string | null
  destination?: string | null
}): string | null {
  const from = shortPlaceName(res.origin)
  const to = shortPlaceName(res.destination)
  if (!from || !to) return null
  return `${from} → ${to}`
}

/**
 * Passageiros que o MOTORISTA ainda pode avaliar, um por pessoa.
 *
 * - Só reserva `confirmed` conta (a API deriva `passengerIds` das confirmadas).
 * - Quem já avaliou (`ratedTargetIds`) fica de fora.
 * - O próprio motorista nunca entra na lista.
 * - Passageiro sem `passengerId` é ignorado: sem id não há alvo válido
 *   (o link `?target=` ficaria quebrado).
 * - `count` conta só as reservas confirmadas — 2 assentos confirmados são
 *   2 lugares na mesma viagem, e o "+1" do card precisa bater com o que o
 *   formulário de avaliação mostra.
 */
export function pendingDriverTargets(
  reservations: RatingTargetReservation[] | null | undefined,
  opts: { ratedTargetIds?: Iterable<string> | null; selfId?: string | null } = {}
): RatingTarget[] {
  const rated = new Set([...(opts.ratedTargetIds || [])].map(String))
  const selfId = opts.selfId ? String(opts.selfId) : null
  const byPassenger = new Map<string, RatingTarget>()

  for (const r of reservations || []) {
    if (r.status !== "confirmed") continue
    const id = r.passengerId ? String(r.passengerId) : ""
    if (!id) continue
    if (selfId && id === selfId) continue
    if (rated.has(id)) continue

    const existing = byPassenger.get(id)
    if (existing) {
      existing.count += 1
      continue
    }
    byPassenger.set(id, {
      id,
      name: r.name || "",
      avatar: r.avatar || null,
      label: shortSegmentLabel(r),
      count: 1,
    })
  }

  return Array.from(byPassenger.values())
}

/**
 * Alvo do PASSAGEIRO: o motorista da carona.
 *
 * `null` quando não há o que avaliar: o passageiro não tem reserva
 * CONFIRMADA, ou já avaliou o motorista. O `ratedTargetIds` vem do servidor
 * filtrado pelo AUTOR e pela CARONA, então "já avaliei o motorista" é
 * exatamente "o motorista está na lista".
 *
 * O rótulo é o TRECHO do passageiro (o que ele comprou), com fallback para a
 * rota da carona — que é o que o passageiro reconhece como "a viagem".
 */
export function passengerRatingTarget(opts: {
  driver?: { id?: string | null; name?: string | null; avatar?: string | null } | null
  myReservation?: RatingTargetReservation | null
  ratedTargetIds?: Iterable<string> | null
  fallbackLabel?: string | null
}): RatingTarget | null {
  const driverId = opts.driver?.id ? String(opts.driver.id) : ""
  if (!driverId) return null

  // Só passageiro CONFIRMADO avaliou a viagem: pedido pendente não é
  // participante e o servidor recusaria a avaliação.
  const res = opts.myReservation && opts.myReservation.status === "confirmed" ? opts.myReservation : null
  if (!res) return null

  const rated = new Set([...(opts.ratedTargetIds || [])].map(String))
  if (rated.has(driverId)) return null

  const label =
    res?.segment ||
    (res?.origin && res?.destination ? `${res.origin} → ${res.destination}` : opts.fallbackLabel || null)

  return {
    id: driverId,
    name: opts.driver?.name || "Motorista",
    avatar: opts.driver?.avatar || null,
    label,
    count: 1,
  }
}
